// Slopegraph for the "A puding próbája az evés" section: the algorithm's
// pair-strength order on the left, the real competition finish on the right,
// each pair connected by a line so the movement (model error) is visible at a
// glance. Lines stay green when the model was right and turn clay as the pair
// moved further from its predicted rank.

interface Row {
  name: string;
  algo: number; // rank the algorithm gave (1 = strongest)
  vers: number; // where the pair actually finished
}

// Listed in algorithm order (algo 1..13).
const ROWS: Row[] = [
  { name: 'Pekli Feri – Pekli Jakus', algo: 1, vers: 2 },
  { name: 'Bodnár Józsi – Boldizsár Zoli', algo: 2, vers: 1 },
  { name: 'Tóth Gyuri – Chladni Era', algo: 3, vers: 11 },
  { name: 'Pekli Marcell – Dr. Hevesi Edit', algo: 4, vers: 5 },
  { name: 'Bodnár Piros – Mezei Gergő', algo: 5, vers: 4 },
  { name: 'Boldizsár Dorka – Mezei Rudolf', algo: 6, vers: 3 },
  { name: 'Boldizsár Liza – Mező Sanya', algo: 7, vers: 6 },
  { name: 'Mezei Fruzsi – Bodnár Balázs', algo: 8, vers: 7 },
  { name: 'Karig Luca – Vermes Ádám', algo: 9, vers: 10 },
  { name: 'Petrányi Ádám – Bánhalmi Bence', algo: 10, vers: 8 },
  { name: 'Schiller Gábor – Simon Gyula', algo: 11, vers: 12 },
  { name: 'Deutsch Máté – Novák Zalán', algo: 12, vers: 9 },
  { name: 'Breiner Marci – Dénes Máté', algo: 13, vers: 13 },
];

const W = 620;
const PAD_T = 38;
const ROW = 34;
const N = ROWS.length;
const H = PAD_T + (N - 1) * ROW + 16;

const X_LEFT_TICK = 16;
const X_LEFT_LABEL = 250;
const X_LEFT_NODE = 262;
const X_RIGHT_NODE = 358;
const X_RIGHT_LABEL = 370;
const X_RIGHT_TICK = W - 16;

const y = (rank: number) => PAD_T + (rank - 1) * ROW;

// Colour by how far the pair moved between prediction and reality.
function lineColor(delta: number): string {
  if (delta === 0) return 'var(--c-green)';
  if (delta === 1) return 'var(--c-green-mid)';
  if (delta <= 3) return 'var(--c-clay-soft)';
  return 'var(--c-clay)';
}

export default function CikkRankSlope() {
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="A párok algoritmus szerinti erősorrendje és a verseny végeredménye, vonalakkal összekötve"
      style={{ display: 'block', width: '100%', maxWidth: `${W}px`, margin: '0.5rem auto 0', height: 'auto' }}
    >
      {/* Column headers */}
      <text x={X_LEFT_LABEL} y={18} textAnchor="end" className="slope-head">
        Algoritmus szerint
      </text>
      <text x={X_RIGHT_LABEL} y={18} textAnchor="start" className="slope-head">
        Verseny szerint
      </text>

      {/* Connecting lines (drawn first so nodes/labels sit on top) */}
      {ROWS.map((r) => {
        const delta = Math.abs(r.algo - r.vers);
        return (
          <line
            key={`l-${r.name}`}
            x1={X_LEFT_NODE}
            y1={y(r.algo)}
            x2={X_RIGHT_NODE}
            y2={y(r.vers)}
            stroke={lineColor(delta)}
            strokeWidth={delta >= 4 ? 2.6 : 1.7}
            strokeLinecap="round"
            opacity={0.9}
          />
        );
      })}

      {/* Left side: rank tick, pair label, node — positioned by algo rank */}
      {ROWS.map((r) => (
        <g key={`L-${r.name}`}>
          <text x={X_LEFT_TICK} y={y(r.algo)} textAnchor="start" dominantBaseline="middle" className="slope-tick">
            {r.algo}
          </text>
          <text x={X_LEFT_LABEL} y={y(r.algo)} textAnchor="end" dominantBaseline="middle" className="slope-name">
            {r.name}
          </text>
          <circle cx={X_LEFT_NODE} cy={y(r.algo)} r={3} fill={lineColor(Math.abs(r.algo - r.vers))} />
        </g>
      ))}

      {/* Right side: node, pair label, rank tick — positioned by competition rank */}
      {ROWS.map((r) => (
        <g key={`R-${r.name}`}>
          <circle cx={X_RIGHT_NODE} cy={y(r.vers)} r={3} fill={lineColor(Math.abs(r.algo - r.vers))} />
          <text x={X_RIGHT_LABEL} y={y(r.vers)} textAnchor="start" dominantBaseline="middle" className="slope-name">
            {r.name}
          </text>
          <text x={X_RIGHT_TICK} y={y(r.vers)} textAnchor="end" dominantBaseline="middle" className="slope-tick">
            {r.vers}
          </text>
        </g>
      ))}
    </svg>
  );
}
